import { useState, useEffect, useRef } from "react";
import toggleSidebarIcon from "../../assets/images/control.png";
import { useAppContext } from "../../context/AppContext";
import { setItem } from "../../utils/localStorage";
import useLocalization from "../../hooks/useLocalization";
import bhashiniImage from "../../assets/images/bhashini-logo.png";
import picImage from "../../assets/images/picLogo.png";
import { isNonEmptyString } from "../../utils/util";
import {
  Trash2,
  X,
  ChevronDown,
  ChevronUp,
  LogOut,
  MessageCirclePlus,
  MessageCircle,
  Moon,
  Sun,
  MoreVertical,
} from "lucide-react";
import emblem from "../../assets/images/modji.png";
import menuIcon from "../../assets/images/mob_menu_icon.svg";
import { useReduxChat } from "../../hooks/useReduxChat";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import {
  clearInputValue,
  setInputDisabled,
  setIsNewChatClicked,
  setMicButtonDisabled,
  setSendButtonDisabled,
} from "../../store/features/ui/uiSlice";

import {
  deleteDataForTextLockFlow,
  deleteDataForVoiceLockFlow,
  setGrievanceAdditionalComments,
  setGrievanceFileData,
  setIsHideUserInputFields,
  setIsSuggestedQuestionsVisible,
  setSelectedChatId,
  setSelectedSocketLanguageData,
} from "../../store/features/message/textContentSlice";
import { selectSelectedChatId } from "../../store/features/message/textContentSelectors";
import { clearChatConfig, setChatConfig } from "../../utils/chatConfigStorage";
import { getLanguageData } from "../../utils/AppConstants";
import { resetAppChatState } from "../../utils/appReset/resetAppChatState";

const Sidebar = () => {
  const dispatch = useDispatch();
  const selectedChatId = useSelector(selectSelectedChatId);
  const { allChats, selectChat, deleteChatById, clearAllChats } =
    useReduxChat();

  const [open, setOpen] = useState(true);
  const userMenuRef = useRef(null);
  const {
    userData,
    setIsNewChat,
    theme,
    setTheme,
    setSelectedLanguageData,
    setSelectedLanguage,
    sharedApiService,
    logout,
  } = useAppContext();
  const { t, setLanguage } = useLocalization();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);

  /**
   * Handle the deletion of a chat by confirming with the user and deleting the chat from the database if confirmed.
   * @param {string} chatId - The ID of the chat to be deleted.
   */
  const handleDelete = (chatId) => {
    Swal.fire({
      title: t("deleteChatMessage.title"),
      text: t("deleteChatMessage.text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("deleteChatMessage.confirm"),
      cancelButtonText: t("deleteChatMessage.cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        deleteChatById(chatId);
        setMenuOpen(null);
        handleNewChat();
        Swal.fire(
          t("deleteChatMessage.deletedTitle"),
          t("deleteChatMessage.deletedMessage"),
          "success"
        );
      }
    });
  };

  useEffect(() => {
    selectChat(selectedChatId);
  }, [selectedChatId, selectChat]);

  useEffect(() => {
    if (window.innerWidth < 768) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    localStorage.setItem("theme", theme);
  }, [theme]);

  /**
   * Resets the chat state and begins a new chat flow.
   * Sets the initial language, clears the input value, and resets the selected socket language data.
   * Cancels all pending API requests and deletes the data for voice lock flow.
   * Sets the `isSuggestedQuestionsVisible` state to true.
   * If the window width is less than 768px, sets the sidebar to be closed.
   */
  const handleNewChat = () => {
    resetAppChatState({
      dispatch,
      sharedApiService,
      setSelectedLanguageData,
    });

    setIsNewChat(true);
    dispatch(setIsNewChatClicked(true));

    if (window.innerWidth < 768) {
      setOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showUserMenu &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  /**
   * Clears all chats by confirming with the user and deleting all chats from the database if confirmed.
   * If confirmed, it calls `handleNewChat` to reset the chat state and begin a new chat flow.
   */
  const handleClearAllChats = () => {
    Swal.fire({
      title: t("clearAllChatsMessage.title"),
      text: t("clearAllChatsMessage.text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("clearAllChatsMessage.confirm"),
      cancelButtonText: t("clearAllChatsMessage.cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        clearAllChats(selectedChatId);
        handleNewChat();
        Swal.fire(
          t("clearAllChatsMessage.clearedTitle"),
          t("clearAllChatsMessage.clearedMessage"),
          "success"
        );
      }
    });
  };

  const handleChatClick = (chat) => () => {
    const chatList = chat?.chatList;

    if (Array.isArray(chatList) && chatList.length > 0) {
      const validConfig = [...chatList]
        .reverse()
        .find((item) => item?.journey != null);

      if (validConfig) {
        const { updatedAt, ...configWithoutUpdatedAt } = validConfig;

        setChatConfig(configWithoutUpdatedAt);
      }
    }

    dispatch(setSelectedChatId(chat.chatId));
    dispatch(setGrievanceFileData(null));
    dispatch(setGrievanceAdditionalComments(""));
    setSelectedLanguageData({
      code: chat.chatList[0].language,
      label: getLanguageData(chat.chatList[0].language)?.label,
    });
    setSelectedLanguage(getLanguageData(chat.chatList[0].language)?.label);
    setLanguage(chat.chatList[0].language);
    if (window.innerWidth < 768) {
      setOpen(false);
    }
  };

  return (
    <>
      <img
        src={menuIcon}
        alt="icon"
        className={`sm:hidden absolute z-[26] top-6 right-5 ${open && "hidden"}`}
        onClick={() => setOpen(!open)}
      />

      <button
        className={`sm:hidden absolute z-[26] top-6 right-5 ${
          !open && "hidden"
        }`}
        aria-label="Close"
        onClick={() => setOpen(!open)}
      >
        <X size={30} strokeWidth={3} className="text-white font-semibold" />
      </button>
      <div
        className={`${
          open ? "w-full sm:w-72" : "hidden sm:block sm:w-20"
        } h-screen p-5 pt-1 absolute sm:relative z-[25] sm:z-auto duration-300 flex flex-col justify-between `}
        style={{
          background: "linear-gradient(180deg, #FFF4E0 0%, #f3bf7c 50%, #f89b44 100%)",
        }}
        >
          
        <div className="z-10 sm:z-auto">
        <img
          src={toggleSidebarIcon}
          alt="menu icon"
          className={`hidden sm:block absolute cursor-pointer -right-3 top-9 w-7 z-50 border-dark-purple border-2 rounded-full bg-white ${
            !open && "rotate-180"
          }`}
          onClick={() => setOpen(!open)}
        />

          <div>
            <img
              src={emblem}
              alt="emblem"
              style={{
                height: "132px",
                width: "auto",
                margin: "auto",
           
                
              }}
              className={!open ? `hidden`:""}
            />
          </div>
          <div ref={userMenuRef} className="sm:hidden w-full">
            <div
              className="flex items-center gap-3 cursor-pointer bg-gradient-to-b from-[#FE6700] to-[#E17100] dark:bg-[#FE6700] rounded-lg p-1 mt-2 transition-colors"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <img
                src={userData?.profileImage || picImage}
                alt="User"
                className="w-10 h-10 rounded-full object-cover border border-white/30"
              />
              <div className="flex-1 truncate">
                <div className="font-semibold text-white truncate">
                  {userData?.name || "User Name"}
                </div>
                {/* <div className="text-sm text-gray-300 truncate">
                  {userData?.email || "email@example.com"}
                </div> */}
              </div>
              {showUserMenu ? (
                <ChevronUp className="text-white" size={16} />
              ) : (
                <ChevronDown className="text-white" size={16} />
              )}
            </div>

            {showUserMenu && (
              <div className="mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                <div className="py-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut size={16} className="text-red-600" />
                    <span className="text-sm font-medium">{t("logout")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <ul className="pt-4">
            <li
              className="flex rounded-md p-2 cursor-pointer border bg-gradient-to-b from-[#FE6700] to-[#E17100] dark:bg-[#FE6700] text-white  text-sm items-center gap-x-4 mt-1 bg-light-white"
              onClick={handleNewChat}
            >
              <MessageCirclePlus className="text-white" size={24} />
              {open && (
                <span
                  className={`${!open && "hidden"} origin-left duration-200`}
                >
                  {t("newChat")}
                </span>
              )}
            </li>

            {allChats && allChats.length > 0 && (
              <>
                <li className="flex justify-between items-center rounded-md py-4 text-gray-300 text-base gap-x-4 mt-1">
                  {open && (
                    <span className="origin-left duration-200 dark:text-white  font-bold text-[#CA3500]">
                      {t("recentChats")}
                    </span>
                  )}

                  {/* Clear All Chats */}
                  <button
                    onClick={handleClearAllChats}
                    className="text-[#CA3500] hover:text-[#CA3500]"
                  >
                    <Trash2
                      size={16}
                     className="text-[#CA3500] hover:text-[#CA3500]"
                    />
                  </button>
                </li>

                {open &&
                  allChats
                    .slice(-5)
                    .reverse()
                    .map((chat, index) => {
                      const firstUserMessage = chat.chatList.find(
                        (msg) =>
                          msg.senderName !== "Bot" &&
                          isNonEmptyString(msg.content)
                      );

                      return (
                        <li
                          key={chat.chatId}
                          className="px-2 py-3 border border-[#FFAC04] rounded bg-[#FFFFFF] flex justify-between items-center cursor-pointer relative"
                          onClick={handleChatClick(chat)}
                        >
                          <a
                            className="flex items-center space-x-3 w-full overflow-hidden"
                            tabIndex={0}
                          >
                            <span className="w-5 h-5 text-black flex-shrink-0">
                              <MessageCircle className="text-[#CA3500]" size={20} />
                            </span>

                            {open && (
                              <span className="text-[#CA3500] text-sm font-medium truncate">
                                {firstUserMessage?.content
                                  ? firstUserMessage.content.length > 25
                                    ? `${firstUserMessage.content.slice(
                                        0,
                                        25
                                      )}...`
                                    : firstUserMessage.content
                                  : "No content"}
                              </span>
                            )}
                          </a>

                          {/* 3-dot menu */}
                          <div
                            className="relative"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpen(menuOpen === index ? null : index);
                            }}
                          >
                            <MoreVertical
                              size={18}
                              className="text-black hover:text-white cursor-pointer"
                            />

                            {menuOpen === index && (
                              <div className="absolute right-0 mt-2 w-32 bg-white text-black rounded-lg shadow-lg z-50">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(chat.chatId);
                                  }}
                                  className="flex items-center w-full text-left px-3 py-2 text-[14px] hover:bg-gray-100 rounded"
                                >
                                  <Trash2
                                    className="text-red-500 mr-2"
                                    size={16}
                                  />
                                  {t("delete")}
                                </button>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
              </>
            )}
          </ul>
        </div>
        <div className="absolute w-full h-[280px] bottom-0 left-0 opacity-60 z-[-1]"></div>
        <ul className="pb-4 space-y-2 hidden">
          <li
            className="flex items-center gap-x-4 rounded-md p-2 cursor-pointer text-white text-md hover:bg-white/10 hover:bg-light-white"
            onClick={() => {
              const newTheme = theme === "light" ? "dark" : "light";
              setTheme(newTheme);
              setItem("theme", newTheme);
            }}
          >
            {theme === "light" ? (
              <Moon className="text-white" size={24} />
            ) : (
              <Sun className="text-white" size={24} />
            )}
            <span className={`${!open && "hidden"} origin-left duration-200`}>
              {t("theme")}
            </span>
          </li>

          {open && <hr className="border-t border-white my-4 hidden" />}
          {open && (
            <li className="flex justify-between space-x-4 mt-2 hidden">
              <svg
                className="cursor-pointer"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.812 5.01663H6.145C3.855 5.01663 2 6.85163 2 9.11563V14.8836C2 17.1476 3.856 18.9836 6.145 18.9836H17.812C20.102 18.9836 21.957 17.1476 21.957 14.8836V9.11563C21.957 6.85163 20.101 5.01562 17.812 5.01562V5.01663ZM15.009 12.2796L9.552 14.8546C9.51872 14.8707 9.48192 14.878 9.44503 14.876C9.40815 14.874 9.37237 14.8626 9.34103 14.8431C9.3097 14.8235 9.28382 14.7964 9.2658 14.7641C9.24779 14.7318 9.23822 14.6956 9.238 14.6586V9.34963C9.23867 9.3125 9.24872 9.27614 9.26722 9.24395C9.28573 9.21176 9.31208 9.18477 9.34382 9.1655C9.37556 9.14624 9.41167 9.13532 9.44877 9.13377C9.48587 9.13221 9.52276 9.14008 9.556 9.15663L15.014 11.8916C15.0504 11.9098 15.0809 11.9378 15.102 11.9725C15.1232 12.0071 15.1341 12.0471 15.1336 12.0877C15.1331 12.1283 15.1211 12.168 15.0991 12.2021C15.077 12.2362 15.0458 12.2634 15.009 12.2806V12.2796Z"
                  fill="white"
                />
              </svg>

              <svg
                className="cursor-pointer"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.8239 20.3871C14.9931 20.3871 15.1304 20.5244 15.1304 20.6935C15.1304 20.8627 14.9931 21 14.8239 21H5.11004C4.52987 21 4.00154 20.7622 3.61928 20.3791C3.23701 19.9968 3 19.4685 3 18.8891V4.11164C3 3.53064 3.23783 3.00234 3.61928 2.62087L3.63763 2.60411C4.0191 2.23143 4.54023 2.00079 5.11083 2.00079H5.97273C6.14192 2.00079 6.27918 2.13806 6.27918 2.30724C6.27918 2.47643 6.14192 2.61369 5.97273 2.61369H5.11083C4.70621 2.61369 4.33671 2.7765 4.0606 3.04624C3.78207 3.32556 3.61289 3.69984 3.61289 4.11163V18.8891C3.61289 19.3017 3.78128 19.6768 4.05341 19.9482C4.32394 20.2187 4.69904 20.3871 5.11083 20.3871H14.8247L14.8239 20.3871ZM12.7513 9.66374L16.6259 4.95215C16.6809 4.87235 16.7735 4.81967 16.8781 4.81967H18.6042C18.6729 4.82127 18.7415 4.84362 18.7982 4.8907C18.9283 4.99764 18.9474 5.19078 18.8397 5.32086L13.9134 11.311L18.4215 17.697C18.5188 17.8351 18.4853 18.0258 18.3472 18.1232C18.2938 18.1607 18.2323 18.179 18.1717 18.179L15.8255 18.1798C15.7154 18.1798 15.6188 18.1216 15.5645 18.0346L12.2487 13.3365L8.37415 18.0474C8.31909 18.1273 8.22651 18.1799 8.12196 18.1799H6.39579C6.32715 18.1783 6.25852 18.156 6.20186 18.1089C6.07178 18.002 6.05262 17.8088 6.16036 17.6787L11.0867 11.6895L6.57857 5.30259C6.4812 5.16453 6.51472 4.97379 6.65278 4.87644C6.70625 4.83893 6.7677 4.82057 6.82836 4.82057H9.17455C9.28468 4.82057 9.38124 4.87883 9.4355 4.96582L12.7521 9.66474L12.7513 9.66374ZM10.1753 2.6129C10.0061 2.6129 9.86882 2.47564 9.86882 2.30645C9.86882 2.13727 10.0061 2 10.1753 2H19.8884C20.4694 2 20.9977 2.23783 21.3799 2.61928L21.3967 2.63763C21.7694 3.0191 22 3.54023 22 4.11083V18.8883C22 19.4677 21.7622 19.996 21.3791 20.3799C20.996 20.763 20.4677 21 19.8883 21H19.0273C18.8581 21 18.7208 20.8627 18.7208 20.6935C18.7208 20.5244 18.8581 20.3871 19.0273 20.3871H19.8883C20.3009 20.3871 20.676 20.2187 20.9466 19.9482C21.2187 19.6768 21.3871 19.3017 21.3871 18.8892V4.11164C21.3871 3.70623 21.2243 3.33753 20.9545 3.06141C20.6752 2.78289 20.3009 2.61371 19.8891 2.61371H10.1761L10.1753 2.6129Z"
                  fill="white"
                />
              </svg>

              <svg
                className="cursor-pointer"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.017 2H7.947C6.37015 2.00185 4.85844 2.62914 3.74353 3.74424C2.62862 4.85933 2.00159 6.37115 2 7.948L2 16.018C2.00185 17.5948 2.62914 19.1066 3.74424 20.2215C4.85933 21.3364 6.37115 21.9634 7.948 21.965H16.018C17.5948 21.9631 19.1066 21.3359 20.2215 20.2208C21.3364 19.1057 21.9634 17.5938 21.965 16.017V7.947C21.9631 6.37015 21.3359 4.85844 20.2208 3.74353C19.1057 2.62862 17.5938 2.00159 16.017 2V2ZM19.957 16.017C19.957 16.5344 19.8551 17.0468 19.6571 17.5248C19.4591 18.0028 19.1689 18.4371 18.803 18.803C18.4371 19.1689 18.0028 19.4591 17.5248 19.6571C17.0468 19.8551 16.5344 19.957 16.017 19.957H7.947C6.90222 19.9567 5.90032 19.5415 5.16165 18.8026C4.42297 18.0638 4.008 17.0618 4.008 16.017V7.947C4.00827 6.90222 4.42349 5.90032 5.16235 5.16165C5.90122 4.42297 6.90322 4.008 7.948 4.008H16.018C17.0628 4.00827 18.0647 4.42349 18.8034 5.16235C19.542 5.90122 19.957 6.90322 19.957 7.948V16.018V16.017Z"
                  fill="white"
                />
                <path
                  d="M11.9823 6.82031C10.6137 6.82243 9.30184 7.3671 8.33421 8.33492C7.36658 9.30274 6.82216 10.6147 6.82031 11.9833C6.8219 13.3522 7.36634 14.6646 8.33421 15.6327C9.30209 16.6008 10.6144 17.1455 11.9833 17.1473C13.3524 17.1457 14.665 16.6012 15.6331 15.6331C16.6012 14.665 17.1457 13.3524 17.1473 11.9833C17.1452 10.6144 16.6003 9.3022 15.632 8.33451C14.6637 7.36682 13.3512 6.82264 11.9823 6.82131V6.82031ZM11.9823 15.1393C11.1456 15.1393 10.3431 14.8069 9.75139 14.2152C9.15971 13.6236 8.82731 12.8211 8.82731 11.9843C8.82731 11.1476 9.15971 10.3451 9.75139 9.75339C10.3431 9.16171 11.1456 8.82931 11.9823 8.82931C12.8191 8.82931 13.6216 9.16171 14.2132 9.75339C14.8049 10.3451 15.1373 11.1476 15.1373 11.9843C15.1373 12.8211 14.8049 13.6236 14.2132 14.2152C13.6216 14.8069 12.8191 15.1393 11.9823 15.1393Z"
                  fill="white"
                />
                <path
                  d="M17.155 8.09119C17.8381 8.09119 18.392 7.53736 18.392 6.85419C18.392 6.17101 17.8381 5.61719 17.155 5.61719C16.4718 5.61719 15.918 6.17101 15.918 6.85419C15.918 7.53736 16.4718 8.09119 17.155 8.09119Z"
                  fill="white"
                />
              </svg>

              <svg
                className="cursor-pointer"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21.959 13.7164V21.0954H17.681V14.2104C17.681 12.4804 17.062 11.3004 15.514 11.3004C14.332 11.3004 13.628 12.0964 13.319 12.8654C13.206 13.1404 13.177 13.5234 13.177 13.9084V21.0954H8.897C8.897 21.0954 8.955 9.43544 8.897 8.22644H13.177V10.0504L13.149 10.0924H13.177V10.0504C13.745 9.17544 14.76 7.92444 17.033 7.92444C19.848 7.92444 21.959 9.76444 21.959 13.7164ZM4.421 2.02344C2.958 2.02344 2 2.98344 2 4.24644C2 5.48144 2.93 6.47044 4.365 6.47044H4.393C5.886 6.47044 6.813 5.48144 6.813 4.24644C6.787 2.98344 5.887 2.02344 4.422 2.02344H4.421ZM2.254 21.0954H6.532V8.22644H2.254V21.0954Z"
                  fill="white"
                />
              </svg>
            </li>
          )}

          {open && (
            <li className="flex justify-center items-center mt-2 bg-white rounded-md w-full px-2 h-[30px]">
              <div className="flex items-center space-x-1 w-full max-w-screen-sm justify-center">
                <span className="text-black text-xs font-medium">
                  {t("navbar.poweredBy")}
                </span>
                <div className="hover:scale-105 transform transition duration-200 ease-in-out">
                  <img
                    src={bhashiniImage}
                    alt="Bhashini Logo"
                    className="h-4 object-contain"
                  />
                </div>
              </div>
            </li>
          )}
        </ul>
      </div>
    </>
  );
};

export default Sidebar;
